import React, { useState, useEffect } from 'react';
import { IconX, IconBrandDiscord, IconLoader2, IconCheck, IconRefresh } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { apiClient } from '../../../../../config/apiConfig';

const COMMON_EMOJIS = ['✅', '👍', '🔥', '💯', '⭐', '🚀', '💪', '🎯'];

const DiscordSyncModal = ({ campaign, onClose, onSyncComplete }) => {
 const campaignId = campaign?._id;
 const briefing = campaign?.briefingMessage;

 // If already synced, pre-fill from existing config
 const [messageId, setMessageId] = useState(briefing?.messageId || '');
 const [channelId, setChannelId] = useState(briefing?.channelId || '');
 const [selectedEmoji, setSelectedEmoji] = useState(briefing?.targetEmoji || '');
 const [messageEmojis, setMessageEmojis] = useState([]);
 const [fetchingMessage, setFetchingMessage] = useState(false);
 const [syncing, setSyncing] = useState(false);
 const [syncedMessage, setSyncedMessage] = useState(null);
 const isSynced = !!briefing?.messageId;

 // Fetch message details to get available emojis
 const handleFetchMessage = async () => {
 if (!messageId.trim()) {
 toast.error('Please enter a Discord message ID');
 return;
 }

 setFetchingMessage(true);
 try {
 const params = {};
 if (channelId.trim()) params.channelId = channelId.trim();

 const response = await apiClient.get(`/discord/messages/${messageId.trim()}`, { params });
 const msg = response.data;
 setSyncedMessage(msg);

 // Extract emojis from reactions
 const emojis = [];
 if (msg.reactions && Array.isArray(msg.reactions)) {
 msg.reactions.forEach(r => {
 const emoji = r.emoji?.name || r.emoji || r.name;
 if (emoji) emojis.push(emoji);
 });
 }

 if (emojis.length > 0) {
 setMessageEmojis(emojis);
 if (!selectedEmoji) setSelectedEmoji(emojis[0]);
 toast.success(`Found ${emojis.length} emoji reactions`);
 } else {
 setMessageEmojis([]);
 toast.error('No emoji reactions found. Select one from common emojis.');
 }

 // Auto-set channelId from response if not provided
 if (!channelId.trim() && msg.channelId) {
 setChannelId(msg.channelId);
 }
 } catch (err) {
 const status = err.response?.status;
 if (status === 404) {
 toast.error('Message not found. Check the ID and channel.');
 } else {
 toast.error(err.response?.data?.error || 'Failed to fetch message');
 }
 setSyncedMessage(null);
 setMessageEmojis([]);
 } finally {
 setFetchingMessage(false);
 }
 };

 // Start sync with selected emoji
 const handleStartSync = async () => {
 if (!messageId.trim()) {
 toast.error('Please enter a message ID');
 return;
 }
 if (!selectedEmoji) {
 toast.error('Please select an emoji to track');
 return;
 }

 setSyncing(true);
 try {
 const payload = {
 messageId: messageId.trim(),
 channelId: channelId.trim() || undefined,
 targetEmoji: selectedEmoji,
 syncMethod: 'Manual'
 };

 await apiClient.post(`/discord/campaigns/${campaignId}/sync-message`, payload);
 toast.success('Discord sync started! Creators will appear automatically.');

 if (onSyncComplete) {
 onSyncComplete({
 messageId: messageId.trim(),
 channelId: channelId.trim(),
 targetEmoji: selectedEmoji
 });
 }
 onClose();
 } catch (err) {
 toast.error(err.response?.data?.error || 'Failed to start sync');
 } finally {
 setSyncing(false);
 }
 };

 // Re-sync existing message
 const handleResync = async () => {
 if (!briefing?.messageId || !briefing?.targetEmoji) return;

 setSyncing(true);
 try {
 const payload = {
 messageId: briefing.messageId,
 channelId: briefing.channelId || undefined,
 targetEmoji: briefing.targetEmoji,
 syncMethod: 'Manual'
 };

 await apiClient.post(`/discord/campaigns/${campaignId}/sync-message`, payload);
 toast.success('Re-synced reactions successfully');

 if (onSyncComplete) onSyncComplete();
 } catch (err) {
 toast.error(err.response?.data?.error || 'Failed to re-sync');
 } finally {
 setSyncing(false);
 }
 };

 const allEmojis = [...new Set([...messageEmojis, ...COMMON_EMOJIS])];

 return (
 <div
 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
 onClick={onClose}
 >
 <div
 className="ui-motion-modal bg-surface-1 border border-glass-border w-full max-w-lg mx-2 sm:mx-4 max-w-[calc(100vw-1rem)]"
 onClick={(e) => e.stopPropagation()}
 >
 {/* Header */}
 <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-glass-border">
 <div className="flex items-center gap-2 min-w-0">
 <IconBrandDiscord size={22} className="text-accent flex-shrink-0" />
 <h2 className="text-lg font-bold text-text-primary truncate">
 {isSynced ? 'Discord Reaction Sync' : 'Set Up Discord Sync'}
 </h2>
 </div>
 <button
 onClick={onClose}
 className="text-text-tertiary hover:text-text-primary flex-shrink-0"
 >
 <IconX size={22} />
 </button>
 </div>

 <div className="px-4 sm:px-6 py-5 space-y-5">
 {/* Already synced status */}
 {isSynced && (
 <div className="flex items-center gap-3 p-3 bg-accent-muted border border-accent/30 flex-wrap">
 <IconCheck size={20} className="text-accent flex-shrink-0" />
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-accent">
 Sync is active
 </p>
 <p className="text-xs text-accent/80 truncate">
 Tracking {briefing.targetEmoji} on message {briefing.messageId}
 </p>
 </div>
 <button
 onClick={handleResync}
 disabled={syncing}
 className="px-3 py-1.5 text-xs font-medium bg-accent hover:bg-accent-hover text-white disabled:opacity-50 flex items-center gap-1.5"
 >
 {syncing ? <IconLoader2 size={14} className="animate-spin" /> : <IconRefresh size={14} />}
 Re-sync
 </button>
 </div>
 )}

 {/* Message ID */}
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">
 Discord Message ID
 </label>
 <div className="flex gap-2">
 <input
 type="text"
 value={messageId}
 onChange={(e) => setMessageId(e.target.value)}
 onKeyDown={(e) => e.key === 'Enter' && handleFetchMessage()}
 placeholder="Paste the Discord message ID"
 className="flex-1 min-w-0 px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 <button
 onClick={handleFetchMessage}
 disabled={fetchingMessage || !messageId.trim()}
 className="px-3 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
 >
 {fetchingMessage ? <IconLoader2 size={16} className="animate-spin" /> : 'Fetch'}
 </button>
 </div>
 </div>

 {/* Channel ID (optional) */}
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">
 Channel ID <span className="text-text-tertiary font-normal">(optional)</span>
 </label>
 <input
 type="text"
 value={channelId}
 onChange={(e) => setChannelId(e.target.value)}
 placeholder="Auto-detected if left empty"
 className="w-full px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 </div>

 {/* Emoji Selection */}
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">
 Track Emoji
 </label>
 {messageEmojis.length > 0 && (
 <p className="text-xs text-text-tertiary mb-2">
 Emojis found on message:
 </p>
 )}
 <div className="flex flex-wrap gap-2">
 {allEmojis.map((emoji) => (
 <button
 key={emoji}
 onClick={() => setSelectedEmoji(emoji)}
 className={`w-10 h-10 flex items-center justify-center text-lg border-2 min-h-[44px] ${
 selectedEmoji === emoji
 ? 'border-accent bg-accent-muted'
 : 'border-glass-border hover:border-white/20'
 } ${messageEmojis.includes(emoji) ? 'ring-1 ring-accent' : ''}`}
 >
 {emoji}
 </button>
 ))}
 </div>
 </div>

 {/* Message preview if fetched */}
 {syncedMessage && !syncedMessage.notFound && (
 <div className="p-3 bg-surface-0 border border-glass-border">
 <p className="text-xs font-medium text-text-tertiary mb-1">Message Preview</p>
 <p className="text-sm text-text-secondary line-clamp-3">
 {syncedMessage.content || 'No text content'}
 </p>
 </div>
 )}
 </div>

 {/* Footer */}
 <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-glass-border">
 <button
 onClick={onClose}
 className="px-4 py-2 text-sm font-medium border border-glass-border text-text-primary hover:bg-glass-bg-hover min-h-[44px]"
 >
 Cancel
 </button>
 <button
 onClick={handleStartSync}
 disabled={syncing || !messageId.trim() || !selectedEmoji}
 className="px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white disabled:opacity-50 flex items-center gap-2 min-h-[44px]"
 >
 {syncing && <IconLoader2 size={16} className="animate-spin" />}
 {isSynced ? 'Update Sync' : 'Start Sync'}
 </button>
 </div>
 </div>
 </div>
 );
};

export default DiscordSyncModal;
